import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase/config';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Layout from './components/Layout';
// Eager: the landing feed and login render instantly; everything else is
// route-split so the initial bundle stays small.
import Feed from './pages/Feed';
import Login from './pages/Login';
import ProtectedAdminRoute from './components/ProtectedAdminRoute';

const Home = lazy(() => import('./pages/Home'));
const KinshipWayStore = lazy(() => import('./pages/KinshipWayStore'));
const Welcome = lazy(() => import('./pages/Welcome'));
const Agreements = lazy(() => import('./pages/Agreements'));
const GlobalFeed = lazy(() => import('./pages/GlobalFeed'));
const Community = lazy(() => import('./pages/Community'));
const LivingKinshipWay = lazy(() => import('./pages/LivingKinshipWay'));
const CulturalPosts = lazy(() => import('./pages/CulturalPosts'));
const Events = lazy(() => import('./pages/Events'));
const Marketplace = lazy(() => import('./pages/Marketplace'));
const StorePage = lazy(() => import('./pages/StorePage'));
const LearningHub = lazy(() => import('./pages/LearningHub'));
const Tribes = lazy(() => import('./pages/Tribes'));
const PickYourKin = lazy(() => import('./pages/PickYourKin'));
const Profile = lazy(() => import('./pages/Profile'));
const ViewProfile = lazy(() => import('./pages/ViewProfile'));
const ViewPost = lazy(() => import('./pages/ViewPost'));
const About = lazy(() => import('./pages/About'));
const Terms = lazy(() => import('./pages/Terms'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Safety = lazy(() => import('./pages/Safety'));
const Support = lazy(() => import('./pages/Support'));
const ContactUs = lazy(() => import('./pages/ContactUs'));
const MessagingSystem = lazy(() => import('./components/MessagingSystem'));
const NearbyUsers = lazy(() => import('./components/NearbyUsers'));
const DatabaseSeeder = lazy(() => import('./components/DatabaseSeeder'));
const SimpleTest = lazy(() => import('./components/SimpleTest'));
const FriendManagement = lazy(() => import('./components/FriendManagement'));
const SocialTab = lazy(() => import('./components/SocialTab'));
const CommunityMembers = lazy(() => import('./components/CommunityMembers'));
const AdminSetup = lazy(() => import('./components/AdminSetup'));
const AdminManagement = lazy(() => import('./components/AdminManagement'));
const AdminSetupScript = lazy(() => import('./components/AdminSetupScript'));
const AdminPanel = lazy(() => import('./components/AdminPanel'));
const DeveloperPanel = lazy(() => import('./components/DeveloperPanel'));
const Rewards = lazy(() => import('./pages/Rewards'));
const ReferralInvitation = lazy(() => import('./pages/Rewards').then(m => ({ default: m.ReferralInvitation })));

/** Route-transition skeleton — shimmer cards instead of a jarring spinner. */
const RouteFallback: React.FC = () => (
  <div className="mx-auto w-full max-w-3xl px-4 py-6 space-y-5">
    <div className="skeleton h-24 rounded-3xl" />
    <div className="skeleton h-56 rounded-3xl" />
    <div className="skeleton h-56 rounded-3xl" />
  </div>
);

function App() {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-accent-green rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-2xl">K</span>
          </div>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-blue mx-auto"></div>
          <p className="text-text-muted mt-2">Loading The Kinship Way...</p>
        </div>
      </div>
    );
  }

  return (
    <ThemeProvider>
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="*" element={
            <Layout user={user} onAuthSuccess={() => setUser(auth.currentUser)}>
              <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={user ? <Feed /> : <Home />} />
                <Route path="/home" element={<Home />} />
                <Route path="/earn-rewards" element={<Rewards />} />
                <Route path="/thekincoin" element={<Rewards />} />
                <Route path="/ref/:code" element={<ReferralInvitation />} />
                <Route path="/welcome" element={<Welcome />} />
                <Route path="/agreements" element={<Agreements />} />
                <Route path="/global-feed" element={<GlobalFeed />} />
                <Route path="/feed" element={<Feed />} />
                <Route path="/kinship-way-store" element={<KinshipWayStore />} />
                <Route path="/kinship-way" element={<LivingKinshipWay />} />
                <Route path="/community" element={<Community />} />
                <Route
                  path="/weekly-meet"
                  element={<Navigate to="/community" replace state={{ clearTopicAndScrollWeekly: true }} />}
                />
                <Route path="/cultural-posts" element={<CulturalPosts />} />
                <Route path="/events" element={<Events />} />
                <Route path="/marketplace" element={<Marketplace />} />
                <Route path="/store/:storeId" element={<StorePage />} />
                <Route path="/learning-hub" element={<LearningHub />} />
                <Route path="/tribes" element={<Tribes />} />
                <Route path="/pick-your-kin" element={<PickYourKin />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/profile/:userId" element={<ViewProfile />} />
                <Route path="/post/:postId" element={<ViewPost />} />
                <Route path="/about" element={<About />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/safety" element={<Safety />} />
                <Route path="/support" element={<Support />} />
                <Route path="/contact" element={<ContactUs />} />
                <Route path="/messages" element={<MessagingSystem />} />
                <Route path="/nearby" element={<NearbyUsers />} />
                <Route path="/seed-database" element={<DatabaseSeeder />} />
                <Route path="/test-firebase" element={<SimpleTest />} />
                <Route path="/friends" element={<FriendManagement />} />
                <Route path="/social" element={<SocialTab />} />
                <Route path="/community-members" element={<CommunityMembers />} />
                <Route path="/admin-setup" element={<ProtectedAdminRoute><AdminSetup /></ProtectedAdminRoute>} />
                <Route path="/admin-management" element={<ProtectedAdminRoute><AdminManagement /></ProtectedAdminRoute>} />
                <Route path="/admin-script" element={<ProtectedAdminRoute><AdminSetupScript /></ProtectedAdminRoute>} />
                <Route path="/admin-panel" element={<ProtectedAdminRoute><AdminPanel /></ProtectedAdminRoute>} />
                <Route path="/developer-panel" element={<DeveloperPanel />} />
              </Routes>
              </Suspense>
            </Layout>
          } />
        </Routes>
      </Router>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
